<script setup lang="ts">
    import { VSonner } from 'vuetify-sonner';
    import 'vuetify-sonner/style.css'
    import { onMounted, provide, reactive, ref, watch } from 'vue';
    import OrderSettingsDialog from '@/components/orderSettings/OrderSettingsDialog.vue';
    import eventBus from '@/services/eventBus';

    import { useTheme } from 'vuetify';
    import { useUserStore } from '@/stores/user';
    import { useMenuStore } from '@/stores/menu';
    import { useAppStore } from '@/stores/app';
    import { EventName, Platform } from '@/constants';
    import BetDialog from '@/pages/core/betDialog/BetDialog.vue';
    import { feedService } from '@/services/feedService';
    import { getPlatform } from '@/helpers/getPlatform';
    import { useEventStore } from '@/stores/event';
    import AppMenuMobile from '@/components/menu/AppMenuMobile.vue';
    import AppMenuTBPlayer from '@/components/menu/AppMenuTBPlayer.vue';
    import AppNotification from '@/pages/core/notification/AppNotification.vue';
    import Event from '@/pages/core/event/Event.vue';
    import { useWebSocketStore } from '@/stores/webSocket';
    import AppMenuWLPlayer from '@/components/menu/AppMenuWLPlayer.vue';
    import AppMenuGuest from '@/components/menu/AppMenuGuest.vue';
    import { addGtag } from 'vue-gtag';
    import { useDisplay } from 'vuetify/framework';

    const userStore = useUserStore();
    const menuStore = useMenuStore();
    const appStore = useAppStore();
    const eventStore = useEventStore();
    const wsStore = useWebSocketStore();
    const display = useDisplay();
    const theme = useTheme();
    const { t } = useI18n();
    const route = useRoute();

    const changeOrderSettingsDialogRef = ref(); // Dialog component reference
    const eventRef = ref();

    const isVueGtagEnabled = localStorage.getItem('cookiesPolicy') === 'true';
    const cookiesPolicyDialog = ref(false);

    //manually run Google Analytics
    if (getPlatform() === Platform.Tradeball || isVueGtagEnabled) {
        addGtag();
    } else {
        cookiesPolicyDialog.value = localStorage.getItem('cookiesPolicy') === null;
    }

    watch(
        () => route.name,
        () => {
            if (route.meta.isHideCookiesPolicy) {
                cookiesPolicyDialog.value = false;
            } else {
                if (getPlatform() === Platform.Tradeball || isVueGtagEnabled) {
                    cookiesPolicyDialog.value = false;
                } else {
                    cookiesPolicyDialog.value = localStorage.getItem('cookiesPolicy') === null;
                }
            }
        },
        { immediate: true }
    );

    watch(
        () => appStore.currentTheme,
        (newVal) => {
            theme.change(newVal);
        }
    );

    const betDialogsRefs = reactive<any>({});

    function setBetDialogRef(el: any, id: number) {
        const key = 'betDialog' + id;
        if (el) betDialogsRefs[key] = el;
    }

    function disagreeCookiesPolicy() {
        cookiesPolicyDialog.value = false;
        localStorage.setItem('cookiesPolicy', 'false');
    }
    function agreeCookiesPolicy() {
        cookiesPolicyDialog.value = false;
        localStorage.setItem('cookiesPolicy', 'true');

        addGtag();
    }

    // Provide it for child components
    provide('changeOrderSettingsDialogRef', changeOrderSettingsDialogRef);

    provide('eventRef', eventRef);

    provide('betDialogsRefs', betDialogsRefs);

    /*function onEscTopDialog(e: KeyboardEvent) {
        if (e.key !== 'Escape') return;
        console.log('Escape');
    }*/

    onMounted(() => {
        //window.addEventListener('keydown', onEscTopDialog, { capture: true });

        window.addEventListener('focus', () => {
            wsStore.checkConnection();
        });

        document.addEventListener('visibilitychange', () => {
            console.log(document.hidden ? 'Tab is Inactive' : 'Tab is Active');

            appStore.isBrowserTabInactive = document.hidden;

            if (!document.hidden) {
                const browserTabInactiveTimeSec = (Date.now() - (appStore.lastBrowserTabInactive ?? 0)) / 1000;
                appStore.lastBrowserTabInactive = null;
                eventBus.emit(EventName.BrowserTabVisibilityChanged, { isBrowserTabInactive: false, browserTabInactiveTimeSec: browserTabInactiveTimeSec });
            } else {
                eventBus.emit(EventName.BrowserTabVisibilityChanged, { isBrowserTabInactive: true });
                appStore.lastBrowserTabInactive = Date.now();
                wsStore.checkConnection();
            }
        });
    });
</script>

<template>
    <!--    <transition name="fade">-->
    <v-app ref="main" id="mainId">
        <AppMenuMobile></AppMenuMobile>

        <AppMenuGuest ref="menuGuest" v-if="menuStore.withMenu && !userStore.isLoggedIn() && getPlatform() !== Platform.Tradeball"></AppMenuGuest>
        <AppMenuWLPlayer ref="menuWl" v-else-if="menuStore.withMenu && userStore.isLoggedIn() && getPlatform() !== Platform.Tradeball"></AppMenuWLPlayer>
        <AppMenuTBPlayer ref="menuTb" v-else-if="menuStore.withMenu && userStore.isLoggedIn() && getPlatform() === Platform.Tradeball"></AppMenuTBPlayer>

        <VSonner :expand="true" :visible-toasts="6" />

        <v-main class="d-flex flex-column" :class="menuStore.withMenu ? 'pt-12' : 'pt-0'">
            <v-progress-linear v-if="appStore.loading" color="tbGreen" indeterminate></v-progress-linear>
            <v-progress-linear v-else value="0" color="transparent" />

            <AppNotification v-if="menuStore.withMenu && userStore.isLoggedIn()"></AppNotification>

            <router-view />

            <OrderSettingsDialog ref="changeOrderSettingsDialogRef"></OrderSettingsDialog>

            <Event v-if="eventStore.isDialogOpen"></Event>

            <BetDialog v-for="index in 5" :id="index + ''" :key="index" :ref="(el) => setBetDialogRef(el, index)" @cancel="feedService.wsSubscribe(true)"></BetDialog>

            <v-dialog persistent v-model="cookiesPolicyDialog" width="640" :fullscreen="display.xs.value" :no-click-animation="true">
                <v-card class="font13 pa-3">
                    <v-toolbar dense flat>
                        <v-toolbar-title class="text-h5">{{ t('action.cookiesPolicyTitle') }}</v-toolbar-title>
                    </v-toolbar>
                    <v-card-text class="px-4 py-1 font14">
                        <span>
                            {{ t('action.cookiesPolicyText') }}
                            <router-link :to="{ name: 'cookies-policy' }" style="text-decoration: none; color: #6aa84f">{{ t('menu.cookies') }}</router-link>
                        </span>
                    </v-card-text>
                    <v-card-actions class="">
                        <v-spacer></v-spacer>
                        <v-btn tag="a" color="tbGreen" style="color: white" elevation="0" density="default" variant="outlined" @click="disagreeCookiesPolicy()">
                            {{ t('action.cookiesPolicyReject') }}
                        </v-btn>

                        <v-btn tag="a" color="tbGreen" style="color: white" elevation="0" variant="flat" density="default" @click="agreeCookiesPolicy()">
                            {{ t('action.cookiesPolicyAccept') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <div id="tooltip" class="tooltip"></div>
        </v-main>
    </v-app>
    <!--    </transition>-->
</template>
